// @ts-strict-ignore
import React from 'react'
import capitalize from 'lodash-es/capitalize'
import { useTranslation } from 'react-i18next'
import {
  BankAccountPaymentInstrument,
  Contact,
  ContactPaymentInstrument,
  CryptoContactInstrument,
  InstrumentTypes,
  RelationshipTypes,
  WalletContactInstrumentEligibility,
} from '@node-space/types'
import { ActionListItem, Avatar, Box, Highlight, Tooltip } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import {
  getContactName,
  getIneligibleBankAccountMessage,
} from 'pages/Wallets/components/FiatSend/utils'
import { ContactInstrumentsListProps } from './ContactInstrumentsListV1'
import { ContactOrPaymentInstrumentList } from './ContactOrPaymentInstrumentList'

interface BankAccountItemProps {
  index: number
  isFirstParty: boolean
  contactName: string
  ineligibleBankAccountMessage?: string
  paymentInstrument: BankAccountPaymentInstrument | CryptoContactInstrument
  contact: Contact
  eligibility?: WalletContactInstrumentEligibility[]
  onSelect: (item: ContactPaymentInstrument) => void
}

interface CryptoItemProps {
  index: number
  isFirstParty: boolean
  contactName: string
  paymentInstrument: BankAccountPaymentInstrument | CryptoContactInstrument
  contact: Contact
  eligibility?: WalletContactInstrumentEligibility[]
  onSelect: (item: ContactPaymentInstrument) => void
}

const BankAccountPaymentInstrumentItem = ({
  paymentInstrument,
  contact,
  eligibility,
  index,
  isFirstParty,
  contactName,
  ineligibleBankAccountMessage,
  onSelect,
}: BankAccountItemProps) => {
  const instrument = paymentInstrument as BankAccountPaymentInstrument
  const { accountNumber, bank } = instrument || {}
  const { identificationCode, swiftCode } = bank || {}

  const secondaryText = !ineligibleBankAccountMessage
    ? {
        BodyPre:
          (accountNumber?.length || 0) > 20
            ? ellipseMiddleText(accountNumber || '', 6, 8)
            : accountNumber,
        BodyPost: identificationCode || swiftCode,
      }
    : { BodyPre: ineligibleBankAccountMessage }

  return (
    <ActionListItem
      key={instrument?.id}
      id={instrument?.id}
      testId={`contacts-step-list-item-${index}`}
      {...(!ineligibleBankAccountMessage && {
        onItemClick: () => onSelect({ paymentInstrument, contact, eligibility }),
      })}
      disabled={!!ineligibleBankAccountMessage}
      title={
        <Tooltip bodyContent={contact?.name} type="simple">
          <p>{instrument?.description}</p>
        </Tooltip>
      }
      Avatar={<Avatar size="default" type="currency" name={instrument?.currency?.toLowerCase()} />}
      BodyPre={instrument?.accountNumber}
      {...secondaryText}
      ActionHighlight={
        isFirstParty ? (
          <Highlight variant="emphasis" type="feature">
            My company
          </Highlight>
        ) : undefined
      }
      RightPrimaryText={!isFirstParty ? contactName : undefined}
    />
  )
}

const CryptoPaymentInstrumentItem = ({
  paymentInstrument,
  contact,
  eligibility,
  index,
  isFirstParty,
  contactName,
  onSelect,
}: CryptoItemProps) => {
  const instrument = paymentInstrument as CryptoContactInstrument

  return (
    <ActionListItem
      key={contact?.id}
      id={contact?.id}
      title={contact?.name}
      testId={`contacts-step-list-item-${index}`}
      Avatar={<Avatar size="default" type="currency" name={instrument?.currency?.toLowerCase()} />}
      BodyPre={<p className="text-label min-w-[0px] line-clamp-1">{instrument?.description}</p>}
      onItemClick={() => onSelect({ paymentInstrument, contact, eligibility })}
      BodyPost={
        <Tooltip
          bodyContent={`${instrument?.currency} (${capitalize(instrument?.network)}) - ${ellipseMiddleText(instrument?.address || '', 6, 6, '*')}`}
          type="simple"
          position="bottom"
        >
          <Box className="cursor-pointer">
            <p className="text-body-regular whitespace-nowrap">{instrument?.currency}</p>
          </Box>
        </Tooltip>
      }
      ActionHighlight={
        isFirstParty ? (
          <Highlight variant="emphasis" type="feature">
            My company
          </Highlight>
        ) : undefined
      }
      RightPrimaryText={!isFirstParty ? contactName : undefined}
    />
  )
}

export const ContactInstrumentsListV2 = ({
  contacts,
  isFetchingNextPage,
  hasLoadedMore,
  hasNextPage,
  handleContactSelect,
  setHasLoadedMore,
  fetchNextPage,
}: ContactInstrumentsListProps) => {
  const { t } = useTranslation()

  return (
    <ContactOrPaymentInstrumentList
      list={contacts}
      isFetchingNextPage={isFetchingNextPage}
      hasLoadedMore={hasLoadedMore}
      hasNextPage={hasNextPage}
      setHasLoadedMore={setHasLoadedMore}
      fetchNextPage={fetchNextPage}
      getItemKey={item => item.contact?.id}
      listItemRenderer={(item, index) => {
        const { paymentInstrument, contact, eligibility } = item
        const isBankAccount = paymentInstrument?.type === InstrumentTypes.BANK_ACCOUNT
        const isFirstParty = contact?.relationshipType === RelationshipTypes.SELF_OWNED
        const contactName = getContactName(contact)
        const ineligibleBankAccountMessage = getIneligibleBankAccountMessage(
          t,
          paymentInstrument?.currency || '',
          eligibility
        )

        return isBankAccount ? (
          <BankAccountPaymentInstrumentItem
            paymentInstrument={paymentInstrument as BankAccountPaymentInstrument}
            contact={contact}
            eligibility={eligibility}
            index={index}
            isFirstParty={isFirstParty}
            contactName={contactName}
            ineligibleBankAccountMessage={ineligibleBankAccountMessage}
            onSelect={handleContactSelect}
          />
        ) : (
          <CryptoPaymentInstrumentItem
            paymentInstrument={paymentInstrument as CryptoContactInstrument}
            contact={contact}
            eligibility={eligibility}
            index={index}
            isFirstParty={isFirstParty}
            contactName={contactName}
            onSelect={handleContactSelect}
          />
        )
      }}
    />
  )
}
